// Phase 8.0 (Option C) — "Add workspace" via web-driven login.
// Phase 8.2: open to every user (self-service); the backend enforces
// that a regular user signs in with their OWN Slack account (matched
// by email) — otherwise the workspace is rejected (rejected_not_owner).
//
// Click → step-up password → a noVNC tab opens (the headful Slack login
// running on the server's virtual display) → operator signs into Slack →
// "I've finished signing in" finalises, committing every workspace the
// browser is signed into through the shared login pipeline. The modal
// polls status while the tab is open and renders the per-workspace
// outcome (active / pending attribution / failed).

import { useCallback, useEffect, useRef, useState } from "react";

import {
  ApiError,
  buildNoVncUrl,
  cancelLoginSession,
  finalizeLoginSession,
  getLoginSessionStatus,
  startWorkspaceLogin,
  type LoginSessionStatus,
  type LoginTeamResult,
  type StartLoginResponse,
} from "../lib/api";
import { Modal } from "./ui/Modal";

type Phase = "intro" | "running" | "finalizing" | "done";

export interface AddWorkspaceModalProps {
  onClose: () => void;
  /** Fired after a finalize commits, so callers can refresh status. */
  onCommitted?: () => void;
}

export function AddWorkspaceModal({ onClose, onCommitted }: AddWorkspaceModalProps) {
  const [phase, setPhase] = useState<Phase>("intro");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<StartLoginResponse | null>(null);
  const [status, setStatus] = useState<LoginSessionStatus | null>(null);
  const winRef = useRef<Window | null>(null);

  const handleStart = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password) return;
      setBusy(true);
      setError(null);
      try {
        const s = await startWorkspaceLogin(password);
        setSession(s);
        // Open the noVNC login tab. A blocked popup leaves winRef null;
        // we surface a manual link in the running view as a fallback.
        winRef.current = window.open(
          buildNoVncUrl(s),
          "slack-archive-login",
          "width=1100,height=820",
        );
        setPhase("running");
      } catch (err) {
        setError(
          err instanceof ApiError && err.status === 401
            ? "Incorrect password."
            : err instanceof Error
              ? err.message
              : "Failed to start the login.",
        );
      } finally {
        setBusy(false);
      }
    },
    [password],
  );

  // Poll status while the operator is signing in, so a login finalised
  // elsewhere (or that errors / times out) still updates the modal.
  useEffect(() => {
    if (phase !== "running" || !session) return;
    let stop = false;
    const tick = async () => {
      try {
        const st = await getLoginSessionStatus(session.id);
        if (stop) return;
        setStatus(st);
        if (st.state === "done" || st.state === "cancelled" || st.state === "error") {
          setPhase("done");
        }
      } catch {
        /* transient — keep polling */
      }
    };
    void tick();
    const iv = window.setInterval(tick, 4000);
    return () => {
      stop = true;
      window.clearInterval(iv);
    };
  }, [phase, session]);

  const handleFinalize = useCallback(async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    setPhase("finalizing");
    try {
      const st = await finalizeLoginSession(session.id);
      setStatus(st);
      setPhase("done");
      onCommitted?.();
      try {
        winRef.current?.close();
      } catch {
        /* tab may already be closed */
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Finalising failed.");
      setPhase("running");
    } finally {
      setBusy(false);
    }
  }, [session, onCommitted]);

  const handleCancel = useCallback(async () => {
    if (session) {
      try {
        await cancelLoginSession(session.id);
      } catch {
        /* best-effort */
      }
    }
    try {
      winRef.current?.close();
    } catch {
      /* ignore */
    }
    onClose();
  }, [session, onClose]);

  // Esc / backdrop: cancel an in-flight login; just close otherwise.
  const requestClose = phase === "intro" || phase === "done" ? onClose : handleCancel;

  return (
    <Modal
      open
      onClose={requestClose}
      title="Add Slack workspace"
      size="md"
      busy={busy}
    >
      {phase === "intro" && (
        <form onSubmit={handleStart} className="space-y-4" data-testid="add-ws-intro">
          <p className="text-sm text-theme-text-secondary leading-relaxed">
            A secure login window will open in a new tab. Sign in to Slack as
            the workspace owner, then come back here and finish — the server
            captures the session and starts archiving.
          </p>
          <div className="space-y-1.5">
            <label
              htmlFor="add-ws-pw"
              className="block font-mono text-xs uppercase tracking-widest text-theme-text-tertiary"
            >
              Confirm your password
            </label>
            <input
              id="add-ws-pw"
              data-testid="add-ws-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              className="w-full px-3.5 py-2.5 rounded-xl glass-input text-theme-text-primary focus:outline-none focus:ring-2 focus:ring-theme-focus-ring"
            />
          </div>
          {error && (
            <p data-testid="add-ws-error" className="text-sm text-theme-danger-text">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm text-theme-text-secondary hover:text-theme-text-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="add-ws-start"
              disabled={busy || !password}
              className="px-4 py-2 rounded-lg text-sm bg-theme-accent-bg text-theme-accent-text disabled:opacity-50"
            >
              {busy ? "Opening…" : "Open login window"}
            </button>
          </div>
        </form>
      )}

      {(phase === "running" || phase === "finalizing") && session && (
        <div className="space-y-4" data-testid="add-ws-running">
          <p className="text-sm text-theme-text-secondary leading-relaxed">
            A login window has opened in a new tab. Sign in to Slack there, wait
            for it to load, then click <strong>I&rsquo;ve finished signing in</strong>.
          </p>
          <p className="font-mono text-xs text-theme-text-tertiary">
            If the tab didn&rsquo;t open,{" "}
            <a
              href={buildNoVncUrl(session)}
              target="_blank"
              rel="noreferrer"
              className="text-theme-accent-link underline"
            >
              open it here
            </a>
            .
          </p>
          {error && (
            <p data-testid="add-ws-error" className="text-sm text-theme-danger-text">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy}
              className="px-4 py-2 text-sm text-theme-text-secondary hover:text-theme-text-primary"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="add-ws-finalize"
              onClick={handleFinalize}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm bg-theme-accent-bg text-theme-accent-text disabled:opacity-50"
            >
              {phase === "finalizing" ? "Finishing…" : "I've finished signing in"}
            </button>
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="space-y-4" data-testid="add-ws-done">
          <ResultsList status={status} />
          <div className="flex justify-end pt-2">
            <button
              type="button"
              data-testid="add-ws-close"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-theme-accent-bg text-theme-accent-text"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ResultsList({ status }: { status: LoginSessionStatus | null }) {
  const results = status?.results ?? [];
  if (status?.state === "error") {
    return (
      <p className="text-sm text-theme-danger-text">
        The login failed: {status.error ?? "unknown error"}.
      </p>
    );
  }
  if (results.length === 0) {
    return (
      <p className="text-sm text-theme-text-secondary">
        No workspaces were committed. The login may have been cancelled or no
        workspace was signed in.
      </p>
    );
  }
  return (
    <ul className="space-y-2" data-testid="add-ws-results">
      {results.map((r, i) => (
        <li
          key={r.team_id ?? i}
          className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2"
        >
          <span className="text-sm text-theme-text-primary truncate">
            {r.team_name || r.team_id || "(unknown workspace)"}
          </span>
          <ResultBadge result={r} />
        </li>
      ))}
    </ul>
  );
}

function ResultBadge({ result }: { result: LoginTeamResult }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: "Archiving", cls: "text-theme-success-text" },
    pending_attribution: { label: "Needs owner", cls: "text-theme-warning-text" },
    // Phase 8.2: the Slack account signed into isn't the initiating
    // user's own — the workspace was rejected, nothing stored. The
    // row's tooltip (detail) carries the full warning.
    rejected_not_owner: { label: "Not your account", cls: "text-theme-danger-text" },
  };
  const m = map[result.status] ?? { label: "Failed", cls: "text-theme-danger-text" };
  return (
    <span
      title={result.detail || result.attributed_email || undefined}
      className={`font-mono text-[11px] uppercase tracking-widest ${m.cls}`}
    >
      {m.label}
    </span>
  );
}
